import type { QuarantineStore, RiskAiColumnConfigInterface } from './types';
import {
  RiskAiDetection,
  RiskAiRiskType,
  SortOrder,
} from '@Quarantine/RiskAI/types';
import {
  PageInterface,
  QuarantineLogTypeEnum,
  QuarantineRiskAILogTypeEnum,
  QuarantineRiskAILogTypeMap,
  TimeRangeInterface,
} from '@Types/log';
import { getTimeRange, LogTimeUnit } from '@Log/shared';
import request from '@Utils/request';
import { StoreApi } from 'zustand';
import { QuarantineLogContentReqDto } from '@Quarantine/utils/types';
import {
  constructSecurityRiskName,
  mapRiskAiDetectedBy,
  riskAiName,
} from '@Quarantine/RiskAI/utils';
import { escapeQueryString } from '@Utils/chart';
import { OperationsApi, QuarantineApi } from '@Apis';
import { TFunction } from 'i18next';
import { QuarantineStoreProps } from './createStore';
import { globalStoreGetter } from '../../../utils/globalStoreGetter';

export type TagFilter =
  | { id: string; name: string }
  | { id: 'all'; name: 'all' };

// RiskAi store slice types
export interface RiskAiStateDefinition {
  filters: {
    tagFilter: {
      currentValue: TagFilter;
      values: TagFilter[];
    };
    securityRiskNameFilter: {
      currentValue: string | 'all';
      values: string[];
    };
    anomalyNameFilter: {
      currentValue: string | 'all';
      values: string[];
    };
    timeFilter: {
      currentValue: Exclude<LogTimeUnit, LogTimeUnit.Custom>;
      custom?: TimeRangeInterface;
    };
    serviceFilter: {
      currentValue: QuarantineRiskAILogTypeEnum;
      values: QuarantineRiskAILogTypeEnum[];
    };
    searchFilter: { currentValue: string };
    riskTypeFilter: {
      currentValue: RiskAiRiskType;
    };
    detectedByFilter: {
      currentValue: string;
      values: string[];
    };
  };
  sortOrder: SortOrder;
  securityRiskColumnConfig: RiskAiColumnConfigInterface;
  anomalyColumnConfig: RiskAiColumnConfigInterface;
  customizedAnomalyColumnConfig: RiskAiColumnConfigInterface;
}
export interface RiskAiStateActions {
  onHydrate: () => void;
  onPropsChange: (props: Partial<QuarantineStoreProps>) => void;
  fetchDetections: (params: {
    page: PageInterface;
    timeRange: TimeRangeInterface;
    timestampSort: string;
    orgId: string;
  }) => Promise<{
    status: number;
    data: { detections: RiskAiDetection[] | null; total: number };
  }>;
  exportDetectionAsync: (params: {
    timeRange: TimeRangeInterface;
    timestampSort: string;
    orgId: string;
    riskType: RiskAiRiskType;
    serviceName: QuarantineRiskAILogTypeEnum;
  }) => Promise<any>;

  fetchDetectionById: (params: {
    messageId: string;
    timeRange: TimeRangeInterface;
  }) => Promise<{ status: number; data: RiskAiDetection | null }>;
  fetchDetectionBlobPath: (params: {
    serviceName: string;
    messageId: string;
    timeRange: TimeRangeInterface;
  }) => Promise<{ status: number; data: any | null }>;
  fetchDetectionsPage: (params: {
    page: number;
    size: number;
    sort: SortOrder;
  }) => Promise<{
    status: number;
    data: { detections: RiskAiDetection[] | null; total: number };
  }>;
  setSortOrder: (order: SortOrder) => void;
  populateTagFilters: (detections: RiskAiDetection[]) => void;
  populateRiskNameFilters: (
    detections: RiskAiDetection[],
    filterName: 'securityRiskNameFilter' | 'anomalyNameFilter'
  ) => void;
  populateDetectedByFilters: (detections: RiskAiDetection[]) => void;
  setFilter<F extends keyof RiskAiStateDefinition['filters']>(
    filter: F,
    value: RiskAiStateDefinition['filters'][F]['currentValue']
  ): void;
  setCustomTimeRange: (customTimeRange: TimeRangeInterface) => void;
  setSecurityRiskColumnConfig: (
    columnConfig: RiskAiColumnConfigInterface
  ) => void;
  setAnomalyColumnConfig: (columnConfig: RiskAiColumnConfigInterface) => void;
  setCustomizedAnomalyColumnConfig: (
    columnConfig: RiskAiColumnConfigInterface
  ) => void;
}
export type RiskAiStateType = RiskAiStateDefinition & RiskAiStateActions;

const initialRiskAiState = (): RiskAiStateDefinition => ({
  filters: {
    tagFilter: {
      currentValue: { id: 'all', name: 'all' },
      values: [{ id: 'all', name: 'all' }],
    },
    securityRiskNameFilter: {
      currentValue: 'all',
      values: ['all'],
    },
    anomalyNameFilter: {
      currentValue: 'all',
      values: ['all'],
    },
    timeFilter: {
      currentValue: LogTimeUnit.Last24h,
    },
    serviceFilter: {
      currentValue: QuarantineRiskAILogTypeEnum.All,
      values: [
        QuarantineRiskAILogTypeEnum.All,
        QuarantineRiskAILogTypeEnum.Exchange,
        QuarantineRiskAILogTypeEnum.ExchangeInline,
        QuarantineRiskAILogTypeEnum.Gmail,
        QuarantineRiskAILogTypeEnum.GmailInline,
      ],
    },
    searchFilter: { currentValue: '' },
    riskTypeFilter: {
      currentValue: RiskAiRiskType.SecurityRisk,
    },
    detectedByFilter: {
      currentValue: 'all',
      values: ['all'],
    },
  },
  sortOrder: 'desc',
  securityRiskColumnConfig: {
    order: [
      'timestamp',
      'companyGuid',
      'scanSource',
      'msgSubject',
      'msgFrom',
      'mailbox',
      'detectedBy',
      'threatType',
      'securityRiskName',
      'matchedFilters',
      'action',
    ],
    hideIds: [],
    i18nKeys: {
      timestamp: 'riskAi.table.threat.header.timestamp',
      companyGuid: 'riskAi.table.threat.header.companyGuid',
      msgSubject: 'riskAi.table.threat.header.subject',
      msgFrom: 'riskAi.table.threat.header.sender',
      mailbox: 'riskAi.table.threat.header.recipient',
      securityRiskName: 'riskAi.table.threat.header.securityRiskName',
      matchedFilters: 'riskAi.table.threat.header.analysisTag',
      threatType: 'riskAi.table.threat.header.analysisResult',
      detectedBy: 'riskAi.table.threat.header.detectedBy',
      action: 'riskAi.table.threat.header.action',
      scanSource: 'riskAi.table.threat.header.scanSource',
    },
  },
  anomalyColumnConfig: {
    order: [
      'timestamp',
      'companyGuid',
      'scanSource',
      'msgSubject',
      'msgFrom',
      'mailbox',
      'threatType',
      'anomalyName',
      'matchedFilters',
      'action',
    ],
    hideIds: [],
    i18nKeys: {
      timestamp: 'riskAi.table.threat.header.timestamp',
      companyGuid: 'riskAi.table.threat.header.companyGuid',
      msgSubject: 'riskAi.table.threat.header.subject',
      msgFrom: 'riskAi.table.threat.header.sender',
      mailbox: 'riskAi.table.threat.header.recipient',
      anomalyName: 'riskAi.table.threat.header.anomalyName',
      matchedFilters: 'riskAi.table.threat.header.analysisTag',
      threatType: 'riskAi.table.threat.header.analysisResult',
      action: 'riskAi.table.threat.header.action',
      scanSource: 'riskAi.table.threat.header.scanSource',
    },
  },
  customizedAnomalyColumnConfig: {
    order: [
      'timestamp',
      'companyGuid',
      'scanSource',
      'msgSubject',
      'msgFrom',
      'mailbox',
      'threatType',
      'anomalyName',
      'matchedFilters',
      'action',
    ],
    hideIds: [],
    i18nKeys: {
      timestamp: 'riskAi.table.threat.header.timestamp',
      companyGuid: 'riskAi.table.threat.header.companyGuid',
      msgSubject: 'riskAi.table.threat.header.subject',
      msgFrom: 'riskAi.table.threat.header.sender',
      mailbox: 'riskAi.table.threat.header.recipient',
      anomalyName: 'riskAi.table.threat.header.anomalyName',
      matchedFilters: 'riskAi.table.threat.header.analysisTag',
      threatType: 'riskAi.table.threat.header.analysisResult',
      action: 'riskAi.table.threat.header.action',
      scanSource: 'riskAi.table.threat.header.scanSource',
    },
  },
});

export const createRiskAiState = (params: {
  currentUtcOffset: string;
  store: StoreApi<QuarantineStore>;
  set: StoreApi<QuarantineStore>['setState'];
  get: StoreApi<QuarantineStore>['getState'];
}): RiskAiStateType => {
  const { currentUtcOffset, set, get } = params;

  return {
    ...initialRiskAiState(),

    onHydrate: () => {
      // Update previously stored filters except tagFilter which is populated from detections
      const { filters } = get().riskAi;
      const {
        securityRiskNameFilter,
        timeFilter,
        anomalyNameFilter,
        tagFilter,
      } = filters;
      // Update securityRiskNameFilter currentValue if it's not valid
      if (
        !securityRiskNameFilter.values.includes(
          securityRiskNameFilter.currentValue
        )
      ) {
        set((state) => {
          state.riskAi.filters.securityRiskNameFilter.currentValue = 'all';
          return state;
        });
      }
      // Update anomalyNameFilter currentValue if it's not valid
      if (!anomalyNameFilter.values.includes(anomalyNameFilter.currentValue)) {
        set((state) => {
          state.riskAi.filters.anomalyNameFilter.currentValue = 'all';
          return state;
        });
      }
      // Update serviceFilter currentValue if it's not valid
      if (
        !filters.serviceFilter.values.includes(
          filters.serviceFilter.currentValue
        )
      ) {
        set((state) => {
          state.riskAi.filters.serviceFilter.currentValue =
            QuarantineRiskAILogTypeEnum.All;
          return state;
        });
      }
      // Update tagFilter currentValue if it's not valid
      if (
        !tagFilter.values
          .map(({ id }) => id)
          .includes(tagFilter.currentValue.id)
      ) {
        set((state) => {
          state.riskAi.filters.tagFilter.currentValue = {
            id: 'all',
            name: 'all',
          };
          return state;
        });
      }
      // Update detectedByFilter currentValue if it's not valid
      if (
        !filters.detectedByFilter.values.includes(
          filters.detectedByFilter.currentValue
        )
      ) {
        set((state) => {
          state.riskAi.filters.detectedByFilter.currentValue = 'all';
          return state;
        });
      }
      // Update utcOffset in timeFilter custom range
      if (timeFilter.custom) {
        timeFilter.custom.timezoneOffset = currentUtcOffset;
        set((state) => {
          state.riskAi.filters.timeFilter.custom = timeFilter.custom;
          return state;
        });
      }
    },

    onPropsChange: (props) => {
      set((state) => {
        if (props.utcOffset) {
          state.riskAi.setFilter('timeFilter', LogTimeUnit.All);
        }
        return state;
      });
    },

    setFilter: (filter, value) => {
      set((state) => {
        const previousValue = state.riskAi.filters[filter].currentValue;
        state.riskAi.filters[filter].currentValue = value;
        if (filter === 'timeFilter') {
          // Clear custom time range if it's not custom
          state.riskAi.filters.timeFilter.custom = undefined;
        } else if (filter === 'riskTypeFilter' && previousValue !== value) {
          // Only reset filters when risk type actually changes
          // Reset tag filter and risk name filters when changing risk type (tab)
          state.riskAi.filters.tagFilter.currentValue = {
            id: 'all',
            name: 'all',
          };
          state.riskAi.filters.securityRiskNameFilter.currentValue = 'all';
          state.riskAi.filters.anomalyNameFilter.currentValue = 'all';
          state.riskAi.filters.detectedByFilter.currentValue = 'all';

          // Reset dropdown values to initial state
          state.riskAi.filters.tagFilter.values = [{ id: 'all', name: 'all' }];
          state.riskAi.filters.securityRiskNameFilter.values = ['all'];
          state.riskAi.filters.anomalyNameFilter.values = ['all'];
          state.riskAi.filters.detectedByFilter.values = ['all'];
        }
        return state;
      });
    },

    setCustomTimeRange: (customTimeRange) => {
      set((state) => {
        state.riskAi.filters.timeFilter.custom = customTimeRange;
        return state;
      });
    },

    exportDetectionAsync: async (params) => {
      const { timeRange, timestampSort, orgId, riskType, serviceName } = params;
      const { t, currentOrgId } = get();
      const filters = get().riskAi.filters;
      try {
        const logParams = getAllDetectionsReqParams({
          timeRange,
          timestampSort,
          orgId,
          riskType,
          filters: {
            serviceName,
            detectedBy: filters.detectedByFilter.currentValue,
          },
        });
        logParams.searches = JSON.parse(logParams.searches);
        await OperationsApi.apiTriggerRiskAiContentListExport(logParams);
        window.showNotification({
          titleMsg: t('riskAi.model.exportData.successTitle'),
          bodyMsg: t('riskAi.model.exportData.successBody'),
          severity: 'success',
        });
      } catch (e: any) {
        if (
          e.data?.error?.details?.[0]?.code === 'REPORT;RISKAI_OUT_OF_LIMIT'
        ) {
          const limitation = e.data?.error?.details?.[0]?.limit || 20;
          window.showNotification({
            titleMsg: t('errors.report.export.title'),
            bodyMsg: t('errors.REPORT;RISKAI_OUT_OF_LIMIT', {
              orgName: globalStoreGetter.getOrgsMap().get(currentOrgId),
              limitation,
            }),
            severity: 'error',
          });
        } else if (e.status && e.status === 429) {
          window.showNotification({
            titleMsg: t('errors.report.export.title'),
            bodyMsg: t('errors.report.export.tooFrequent'),
            severity: 'error',
          });
        } else {
          window.showNotification({
            titleMsg: t('errors.report.export.title'),
            severity: 'error',
          });
        }
      }
    },

    fetchDetections: async (params) => {
      const filters = get().riskAi.filters;
      const riskType = filters.riskTypeFilter.currentValue;
      try {
        let res;
        // simulate api behavior in development
        if (import.meta.env.MODE === 'development') {
          const contentListKey = {
            [RiskAiRiskType.SecurityRisk]: 'securityRisk',
            [RiskAiRiskType.Anomaly]: 'anomaly',
            [RiskAiRiskType.CustomizedAnomaly]: 'customizedAnomaly',
          }[riskType];
          res = await request({
            url: `log/contentList/riskAi`,
            method: 'get',
          });
          // paginate contentList
          res.data.contentList = res.data[contentListKey]?.slice(
            (params.page.start - 1) * params.page.size,
            params.page.start * params.page.size
          );
          res.data.total = res.data[contentListKey]?.length ?? 0;
        } else {
          const logParams = getAllDetectionsReqParams({
            ...params,
            riskType: filters.riskTypeFilter.currentValue,
            filters: {
              serviceName: filters.serviceFilter.currentValue,
              expressionId: filters.tagFilter.currentValue.id,
              securityRiskName: filters.securityRiskNameFilter.currentValue,
              anomalyName: filters.anomalyNameFilter.currentValue,
              detectedBy: filters.detectedByFilter.currentValue,
            },
          });
          res = await OperationsApi.apiGetRiskAiContentList(logParams);
        }
        const contentList: any[] = res.data.contentList;
        let detections: RiskAiDetection[] | null = null;
        if (Array.isArray(contentList)) {
          detections = mapContentListToRiskAiDetections(contentList, get().t);
        }
        return {
          data: { detections, total: res.data.total },
          status: res.status ?? 200,
        };
      } catch (e) {
        console.log('Error fetching detections:', e);
        return { data: { detections: null, total: 0 }, status: 500 };
      }
    },

    fetchDetectionById: async (params) => {
      const { messageId } = params;
      try {
        let res;
        // simulate api behavior in development
        if (import.meta.env.MODE === 'development') {
          res = await request({
            url: `log/contentList/riskAi`,
            method: 'get',
          });
          const detection = [
            ...res.data.securityRisk,
            ...res.data.anomaly,
            ...res.data.customizedAnomaly,
          ].find((detection: any) => {
            return detection.messageId === messageId;
          });
          res.data.contentList = detection ? [detection] : [];
        } else {
          const logParams = getSingleDetectionReqParams(params);
          res = await OperationsApi.apiGetRiskAiContentList(logParams);
        }
        let contentList: any[] = res.data.contentList;
        if (!Array.isArray(contentList)) {
          contentList = [];
        }
        const detections = mapContentListToRiskAiDetections(
          contentList,
          get().t
        );
        return { data: detections[0] ?? null, status: res.status ?? 200 };
      } catch {
        return { data: null, status: 500 };
      }
    },

    fetchDetectionBlobPath: async (params) => {
      const { messageId } = params;
      try {
        let res;
        // simulate api behavior in development
        if (import.meta.env.MODE === 'development') {
          res = await request({
            url: `quarantine/contentList/riskAi`,
            method: 'get',
          });
          res.data.contentList = res.data.contentList?.filter(
            (detection: any) => {
              return detection.messageId === messageId;
            }
          );
        } else {
          const logParams = getSingleQuarantineDetectionReqParams(params);
          res = await QuarantineApi.apiGetQuarantineLogContent(logParams);
        }
        let contentList: any[] = res.data.contentList;
        if (!Array.isArray(contentList)) {
          contentList = [];
        }
        return {
          data: contentList[0]?.digestBlobPath ?? null,
          status: res.status ?? 200,
        };
      } catch {
        return { data: null, status: 500 };
      }
    },

    fetchDetectionsPage: async (params) => {
      const { page, size, sort } = params;
      const { timeFilter, serviceFilter, riskTypeFilter } =
        get().riskAi.filters;
      const logParams = {
        page: { start: page, size },
        timeRange:
          timeFilter.custom ??
          getTimeRange(timeFilter.currentValue, QuarantineLogTypeEnum.Exchange),
        timestampSort: sort,
        riskType: riskTypeFilter.currentValue,
        orgId: get().currentOrgId,
        serviceName: serviceFilter.currentValue,
      };
      return get().riskAi.fetchDetections(logParams);
    },

    populateTagFilters: (detections) => {
      const currentValues = get().riskAi.filters.tagFilter.values;
      const existingTagsMap = new Map<string, string>();

      // Preserve existing filter values
      currentValues.forEach(({ id, name }) => {
        existingTagsMap.set(id, name);
      });

      // Add new values from current detections
      detections.forEach((detection) => {
        detection.matchedFilterIds?.forEach((id, index) => {
          if (!existingTagsMap.has(id)) {
            const name = detection.matchedFilters[index] || id;
            existingTagsMap.set(id, name);
          }
        });
      });

      // Ensure 'all' is always present
      if (!existingTagsMap.has('all')) {
        existingTagsMap.set('all', 'all');
      }

      // Create tag filter values maintaining 'all' at the beginning
      const tagFilterValues: TagFilter[] = [{ id: 'all', name: 'all' }];
      Array.from(existingTagsMap.entries())
        .filter(([id]) => id !== 'all')
        .forEach(([id, name]) => {
          tagFilterValues.push({ id, name });
        });

      // Only update if values have actually changed
      const currentIds = currentValues.map(({ id }) => id).sort();
      const newIds = tagFilterValues.map(({ id }) => id).sort();
      const hasChanged =
        currentIds.length !== newIds.length ||
        currentIds.some((id, index) => id !== newIds[index]);

      if (!hasChanged) {
        return;
      }

      set((state) => {
        state.riskAi.filters.tagFilter.values = tagFilterValues;
        return state;
      });
    },

    populateRiskNameFilters: (detections, filterName) => {
      const currentValues = get().riskAi.filters[filterName].values;
      const riskNameFilterValues = new Set<string>(currentValues);

      // Add new values from current detections while preserving existing ones
      riskNameFilterValues.add('all');
      detections.forEach((detection) =>
        riskNameFilterValues.add(detection.securityRiskName)
      );

      const newValues = Array.from(riskNameFilterValues);

      // Only update if values have actually changed
      const hasChanged =
        newValues.length !== currentValues.length ||
        newValues.some((v) => !currentValues.includes(v));

      if (!hasChanged) {
        return;
      }

      set((state) => {
        state.riskAi.filters[filterName].values = newValues;
        return state;
      });
    },

    populateDetectedByFilters: (detections) => {
      const currentValues = get().riskAi.filters.detectedByFilter.values;
      const detectedByFilterValues = new Set<string>(currentValues);

      // Add new values from current detections while preserving existing ones
      detectedByFilterValues.add('all');
      detections.forEach((detection) => {
        if (detection.malwareDetectedBy) {
          detectedByFilterValues.add(detection.malwareDetectedBy);
        }
      });

      const newValues = Array.from(detectedByFilterValues);

      // Only update if values have actually changed
      const hasChanged =
        newValues.length !== currentValues.length ||
        newValues.some((v) => !currentValues.includes(v));

      if (!hasChanged) {
        return;
      }

      set((state) => {
        state.riskAi.filters.detectedByFilter.values = newValues;
        return state;
      });
    },

    setSortOrder: (order) => {
      set((state) => {
        state.riskAi.sortOrder = order;
        return state;
      });
    },

    setSecurityRiskColumnConfig: (columnConfig) => {
      set((state) => {
        state.riskAi.securityRiskColumnConfig.order = columnConfig.order;
        state.riskAi.securityRiskColumnConfig.hideIds = columnConfig.hideIds;
        return state;
      });
    },

    setAnomalyColumnConfig(columnConfig) {
      set((state) => {
        state.riskAi.anomalyColumnConfig.order = columnConfig.order;
        state.riskAi.anomalyColumnConfig.hideIds = columnConfig.hideIds;
        return state;
      });
    },

    setCustomizedAnomalyColumnConfig(columnConfig) {
      set((state) => {
        state.riskAi.customizedAnomalyColumnConfig.order = columnConfig.order;
        state.riskAi.customizedAnomalyColumnConfig.hideIds =
          columnConfig.hideIds;
        return state;
      });
    },
  };
};

function mapContentListToRiskAiDetections(
  contentList: any[],
  t: TFunction<'translation', string>
): RiskAiDetection[] {
  return contentList.map((log: any) => {
    const detection: RiskAiDetection = log;
    detection.msgFrom = (log.msgFrom ?? []).join(', ');
    detection.threatType =
      detection.raiRiskType === RiskAiRiskType.CustomizedAnomaly
        ? log.threatType
        : `${log.threatType} - ${log.raiMatchedRules?.[0]?.threat_type}`;
    detection.matchedFilters = log.raiMatchedRules?.[0]?.matched_filters ?? [];
    detection.matchedFilterIds =
      log.raiMatchedRules?.[0]?.matched_filter_ids ?? detection.matchedFilters;
    detection.securityRiskId = log.raiMatchedRules?.[0]?.id || '';
    detection.securityRiskName = constructSecurityRiskName(
      detection.securityRiskId,
      log.raiMatchedRules?.[0]?.name,
      t
    );
    // Default to 'Security Risk'
    detection.raiRiskType = Object.values(RiskAiRiskType)
      .map((s) => s.toLowerCase())
      .includes(log.raiRiskType?.toLowerCase())
      ? log.raiRiskType
      : RiskAiRiskType.SecurityRisk;

    detection.malwareDetectedBy = log.malwareDetectedBy;
    detection.detectedBy = mapRiskAiDetectedBy(log.malwareDetectedBy, t);

    detection.raiLLMThreatAnalysis = log.raiLLMThreatAnalysis;

    return detection;
  });
}

export function getAllDetectionsReqParams(params: {
  page?: PageInterface;
  timeRange: TimeRangeInterface;
  timestampSort: string;
  orgId: string;
  riskType: RiskAiRiskType;
  filters: {
    serviceName: string;
    expressionId?: string;
    securityRiskName?: string;
    anomalyName?: string;
    detectedBy?: string;
  };
}) {
  const { page, timeRange, timestampSort, orgId, riskType, filters } = params;

  const raiRiskTypeValues = {
    [RiskAiRiskType.SecurityRisk]: [
      RiskAiRiskType.SecurityRisk,
      RiskAiRiskType.SecurityRisk.toLowerCase(),
      'confirmed',
    ],
    [RiskAiRiskType.Anomaly]: [
      RiskAiRiskType.Anomaly,
      RiskAiRiskType.Anomaly.toLowerCase(),
    ],
    [RiskAiRiskType.CustomizedAnomaly]: [
      RiskAiRiskType.CustomizedAnomaly,
      RiskAiRiskType.CustomizedAnomaly.toLowerCase(),
    ],
  }[riskType];

  const {
    serviceName: _serviceName,
    securityRiskName,
    anomalyName,
    expressionId,
    detectedBy,
  } = filters;
  const serviceName: QuarantineRiskAILogTypeEnum =
    {
      riskai: QuarantineRiskAILogTypeEnum.All,
      exchange: QuarantineRiskAILogTypeEnum.Exchange,
      exchangeinline: QuarantineRiskAILogTypeEnum.ExchangeInline,
      gmail: QuarantineRiskAILogTypeEnum.Gmail,
      gmailinline: QuarantineRiskAILogTypeEnum.GmailInline,
    }[_serviceName.toLowerCase()] ?? QuarantineRiskAILogTypeEnum.All;

  const searches: any[] = [
    {
      field: 'securityFilterName',
      type: 'terms',
      values: riskAiName,
    },
    {
      field: 'raiRiskType',
      type: 'terms',
      values: raiRiskTypeValues,
    },
  ];

  if (detectedBy && detectedBy !== 'all') {
    searches.push({
      field: 'malwareDetectedBy',
      type: 'terms',
      values: [detectedBy],
    });
  } else {
    searches.push({
      field: 'malwareDetectedBy',
      type: 'terms',
      values: [
        'Correlated Intelligence',
        'Custom Correlated Intelligence',
        'LLM',
      ],
    });
  }

  if (serviceName !== QuarantineRiskAILogTypeEnum.All) {
    searches.push({
      field: 'scanSource',
      type: 'terms',
      values: [QuarantineRiskAILogTypeMap[serviceName]],
    });
  }

  if (orgId !== 'all') {
    searches.push({
      field: 'companyGuid',
      type: 'terms',
      values: [orgId],
    });
  }

  if (expressionId && expressionId !== 'all') {
    searches.push({
      field: 'tm_rai_matched_rules.matched_filter_ids',
      type: 'match',
      values: [expressionId],
    });
  }

  if (securityRiskName && securityRiskName !== 'all') {
    searches.push({
      field: 'tm_rai_matched_rules.name',
      type: 'match',
      values: [securityRiskName],
    });
  }

  if (anomalyName && anomalyName !== 'all') {
    searches.push({
      field: 'tm_rai_matched_rules.name',
      type: 'match',
      values: [anomalyName],
    });
  }

  const logParams: QuarantineLogContentReqDto = {
    serviceName,
    sort: { field: 'timestamp', direction: timestampSort },
    page,
    searches: JSON.stringify(searches),
    timeRange,
  };
  return logParams;
}

export function getSingleDetectionReqParams(params: {
  messageId: string;
  timeRange: TimeRangeInterface;
}) {
  const { messageId, timeRange } = params;
  const logParams: QuarantineLogContentReqDto = {
    serviceName: 'riskAI',
    sort: { field: 'timestamp', direction: 'desc' },
    page: { start: 1, size: 1 },
    searches: JSON.stringify([
      {
        field: 'securityFilterName',
        type: 'terms',
        values: riskAiName,
      },
      {
        field: 'messageId',
        type: 'fuzzyMatching',
        values: [escapeQueryString('messageId', messageId)],
      },
    ]),
    timeRange,
  };
  return logParams;
}

export function getSingleQuarantineDetectionReqParams(params: {
  serviceName: string;
  messageId: string;
  timeRange: TimeRangeInterface;
}) {
  const { serviceName, messageId, timeRange } = params;
  const logParams: QuarantineLogContentReqDto = {
    serviceName,
    sort: { field: 'timestamp', direction: 'desc' },
    page: { start: 1, size: 100 },
    searches: JSON.stringify([
      {
        field: 'messageId',
        type: 'fuzzyMatching',
        values: [escapeQueryString('messageId', messageId)],
      },
    ]),
    timeRange,
  };
  return logParams;
}
